import React, {
  forwardRef,
  ReactNode,
  isValidElement,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import { HeadingText } from '#components/typography/heading-text/heading-text.tsx';
import type { GDSHeadingLevel } from '#components/typography/heading-text/heading-text.tsx';
import type { GDSCustomizableComponent } from '../../types';
import { styled } from '../../theme';
import type { GDSColor } from '../../theme';
import { GenesisCoreInspector } from '../../test-utils/genesis-core-inspector';

export interface GDSHeadingBarProps extends GDSCustomizableComponent {
  title?: ReactNode;
  headingLevel?: GDSHeadingLevel;
  leadingAction?: ReactNode;
  trailingAction?: ReactNode;
  largeTitle?: boolean;
  backgroundColor?: GDSColor;
  transparency?: 'none' | 'gradient' | 'transparent' | 'withBackgrounds';
}

const DISPLAY_NAME = 'HeadingBar';

const HeadingBarWrapper = styled('div', {
  variants: {
    transparency: {
      none: {
        background: '$$backgroundColor',
      },
      transparent: {
        background: 'transparent',
      },
      gradient: {
        background:
          'linear-gradient(180deg, rgba(0, 0, 0, 0.7) 5.8%, rgba(0, 0, 0, 0) 99.68%)',
      },
      withBackgrounds: {
        background: 'transparent',
      },
    },
  },
});

const HeadingBarBase = styled('div', {
  display: 'flex',
  position: 'relative',
  padding: '$threeQuarters',
  minHeight: '$three',
});

const ActionWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  zIndex: 1,
});

const ActionBackground = styled('div', {
  display: 'flex',
  alignItems: 'center',
  borderRadius: '$pill',
  variants: {
    hasBackgrounds: {
      true: {
        backgroundColor: '$surfaceBackgroundPrimary',
      },
    },
  },
});

const TitleWrapper = styled('div', {
  height: '100%',
  position: 'absolute',
  top: 0,
  insetInlineStart: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  paddingBlockStart: '$threeQuarters',
  paddingBlockEnd: '$threeQuarters',
  paddingInlineStart: 'calc($$padding + $threeQuarters)',
  paddingInlineEnd: 'calc($$padding + $threeQuarters)',
});

const Title = styled(HeadingText, {
  padding: '$quarter $half',
  borderRadius: '$pill',
  textAlign: 'center',
  variants: {
    hasBackgrounds: {
      true: {
        backgroundColor: '$surfaceBackgroundPrimary',
      },
    },
    gradient: {
      true: {
        color: '$onSurfaceTextReversed',
      },
    },
  },
});

export const HeadingBar = forwardRef<HTMLDivElement, GDSHeadingBarProps>(
  (
    {
      title,
      leadingAction,
      trailingAction,
      headingLevel = 'display',
      largeTitle,
      backgroundColor = '$surfaceBackgroundPrimary',
      transparency = 'none',
      css,
      'data-testid': testId = `GDS-${DISPLAY_NAME}`,
      ...props
    },
    ref,
  ) => {
    // Find which action is wider - apply that width to side padding of title wrapper
    const leadingRef = useRef<HTMLDivElement>(null);
    const trailingRef = useRef<HTMLDivElement>(null);
    const [sidePadding, setSidePadding] = useState<null | number>(null);

    const leadingWidth = leadingRef?.current?.offsetWidth ?? 0;
    const trailingWidth = trailingRef?.current?.offsetWidth ?? 0;

    useLayoutEffect(() => {
      if (leadingWidth > trailingWidth) {
        setSidePadding(leadingWidth);
      } else setSidePadding(trailingWidth);
    }, [leadingWidth, trailingWidth]);

    const hasBackgrounds = transparency === 'withBackgrounds';
    // If is a React element, pass it as is.
    // Otherwise, wrap it in a HeadingText.
    const titleComponent = isValidElement(title) ? (
      title
    ) : (
      <Title
        level={headingLevel}
        size={largeTitle ? 'xl' : 'sm'}
        hasBackgrounds={hasBackgrounds}
        gradient={transparency === 'gradient'}
        className="GDS-heading-bar-title"
      >
        {title}
      </Title>
    );

    const getJustifyContent = () => {
      if (leadingAction && trailingAction) return 'space-between';
      if (trailingAction) return 'flex-end';
      return 'flex-start';
    };

    return (
      <GenesisCoreInspector displayName={DISPLAY_NAME}>
        <HeadingBarWrapper
          data-testid={testId}
          className="GDS-heading-bar-wrapper"
          transparency={transparency}
          css={{
            ...css,
            $$backgroundColor: `$colors${backgroundColor}`,
          }}
        >
          <HeadingBarBase
            className="GDS-heading-bar-base"
            ref={ref}
            css={{
              justifyContent: getJustifyContent(),
            }}
            {...props}
          >
            {leadingAction && (
              <ActionWrapper
                ref={leadingRef}
                className="GDS-heading-bar-left-action GDS-heading-bar-leading-action"
              >
                <ActionBackground hasBackgrounds={hasBackgrounds}>
                  {leadingAction}
                </ActionBackground>
              </ActionWrapper>
            )}
            {title && sidePadding !== null && (
              <TitleWrapper
                css={{ $$padding: `${sidePadding}px` }}
                className="GDS-heading-bar-title-wrapper"
              >
                {titleComponent}
              </TitleWrapper>
            )}
            {trailingAction && (
              <ActionWrapper
                ref={trailingRef}
                className="GDS-heading-bar-right-action GDS-heading-bar-trailing-action"
              >
                <ActionBackground hasBackgrounds={hasBackgrounds}>
                  {trailingAction}
                </ActionBackground>
              </ActionWrapper>
            )}
          </HeadingBarBase>
        </HeadingBarWrapper>
      </GenesisCoreInspector>
    );
  },
);

HeadingBar.displayName = DISPLAY_NAME;
